import React, { useEffect, useState } from 'react'
import { withStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import StatusBadge from '../shared/StatusBadge'
import { getAutopay, setAutopay } from '../../store/actions/autopayActions'

const styles = (theme) => ({
    panel: {
        padding: theme.spacing(1.75, 2),
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.field,
        display: 'grid',
        gap: theme.spacing(1)
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap'
    },
    title: {
        fontWeight: 600
    },
    small: {
        fontSize: '0.82rem',
        color: theme.palette.text.secondary
    },
    error: {
        fontSize: '0.85rem',
        color: theme.palette.error.main
    }
})

const MESSAGES = {
    no_payment_method: 'Add a card or bank account below to turn on autopay.',
    bank_not_verified: 'Verify your bank account below to use it for autopay.'
}

/**
 * Billing card: turn autopay on or off for the member's own invoiced billing.
 * refreshKey changes when the payment methods on the page change, so the
 * panel re-reads which method autopay would use.
 */
const AutopayPanel = ({ classes, refreshKey }) => {
    const [state, setState] = useState(null)
    const [busy, setBusy] = useState(false)
    const [confirmOff, setConfirmOff] = useState(false)
    const [error, setError] = useState(null)
    const loginToken = localStorage.getItem('loginToken')

    useEffect(() => {
        let live = true
        getAutopay(loginToken, (err, data) => {
            if (live) setState(err ? { failed: true } : data)
        })
        return () => { live = false }
    }, [loginToken, refreshKey])

    const change = (enabled) => {
        setBusy(true)
        setError(null)
        setAutopay(loginToken, enabled, (err, data) => {
            setBusy(false)
            setConfirmOff(false)
            if (err) {
                setError(MESSAGES[err.code] || err.message || 'Autopay could not be changed. Please try again.')
                return
            }
            setState(data)
        })
    }

    if (!state || state.failed || !state.hasBilling) return null
    const method = state.method

    // Autopay is on but there is nothing it can charge: say so, and offer the
    // way out (invoices) next to adding a card or bank account below.
    if (state.autopay && !(method && method.usable)) {
        return <div className={classes.panel} data-testid="autopay-nothing-on-file">
            <div className={classes.row}>
                <span><StatusBadge label="Autopay on: nothing to charge" tone="warn" /></span>
                <Button size="small" variant="outlined" onClick={() => change(false)} disabled={busy}>
                    {busy ? <CircularProgress size={14} /> : 'Switch to invoices'}
                </Button>
            </div>
            <Typography variant="body2">
                {method && method.reason === 'bank_not_verified'
                    ? 'Your bank account is not verified yet, so autopay cannot charge it. Verify it below, or switch to emailed invoices.'
                    : 'There is no card or bank account on file, so your next payment will fail. Add one below, or switch to emailed invoices.'}
            </Typography>
            <Typography className={classes.small}>Emailed invoices are due within 7 days.</Typography>
            {error && <Typography className={classes.error}>{error}</Typography>}
        </div>
    }

    if (state.autopay) {
        return <div className={classes.panel} data-testid="autopay-on">
            <div className={classes.row}>
                <span><StatusBadge label="Autopay on" tone="ok" /></span>
                {!confirmOff
                    ? <Button size="small" onClick={() => setConfirmOff(true)} disabled={busy}>Turn off</Button>
                    : <span style={{ display: 'inline-flex', gap: 8 }}>
                        <Button size="small" onClick={() => setConfirmOff(false)} disabled={busy}>Keep autopay</Button>
                        <Button size="small" variant="outlined" onClick={() => change(false)} disabled={busy}>
                            {busy ? <CircularProgress size={14} /> : 'Switch to invoices'}
                        </Button>
                    </span>}
            </div>
            <Typography variant="body2">
                {method ? `Your membership is charged to ${method.label} each month.` : 'Your membership is charged automatically each month.'}
            </Typography>
            {confirmOff && <Typography className={classes.small}>You'll get an emailed invoice each month instead, due within 7 days.</Typography>}
            {error && <Typography className={classes.error}>{error}</Typography>}
        </div>
    }

    if (!state.invoiced) return null

    const usable = method && method.usable
    return <div className={classes.panel} data-testid="autopay-off">
        <div className={classes.row}>
            <div>
                <Typography className={classes.title}>You're billed by emailed invoice each month</Typography>
                <Typography variant="body2" color="textSecondary">
                    {usable
                        ? `Pay automatically with ${method.label} instead?`
                        : method && method.reason === 'bank_not_verified'
                            ? MESSAGES.bank_not_verified
                            : MESSAGES.no_payment_method}
                </Typography>
            </div>
            {usable && <Button variant="contained" color="primary" onClick={() => change(true)} disabled={busy}>
                {busy ? <CircularProgress size={16} color="inherit" /> : 'Turn on autopay'}
            </Button>}
        </div>
        {usable && <Typography className={classes.small}>
            Starts with your next bill; invoices already sent are paid as usual. By turning on autopay you agree to the <a href="#/recurring-payment-terms" target="_blank" rel="noopener noreferrer">recurring payment terms</a>.
        </Typography>}
        {error && <Typography className={classes.error}>{error}</Typography>}
    </div>
}

export default withStyles(styles)(AutopayPanel)
